import { Hourglass } from 'lucide-react';
import {
  DayOfWeek,
  IntervalUnit,
  LBNodeData,
  LocationTarget,
} from '@/proto/logiblocks/logiblocks';
import { CONTROL_FLOW_ICON_COLOR } from '../../styles';
import {
  type GraphData,
  type LBNodeInfo,
  NodeCategory,
  OutputType,
} from '../../types';
import { collectErrors, validateRequiredArray } from '../../fieldValidation';
import { MSG_REQUIRED } from '../../validationMessages';
import { DelayActionCustomization } from './DelayActionCustomization';
import { DEFAULT_DELAY_ACTION } from './defaults';
import { getDelayRunStepSummary } from './delayRunStepSummary';
import { DelayUntilEventFields } from './delayUntilEventFields';
import { untilEventSubtitle, validateUntilEvent } from './delayUntilEventModel';
import { buildNodeData } from '../../nodeDataUtils';
import { formatUtcSecondsAsUserTime } from '@/pages/Logiblocks/dateTimeUtils';
import { secondsToAmount } from '@/pages/Logiblocks/intervalUnits';

/** Validation field names for the Delay action. Shared with the UI. */
export const DelayActionFields = {
  DAYS_OF_WEEK: 'Days of week',
  DELAY_DURATION: 'Delay duration',
  TIME: 'Time',
  ...DelayUntilEventFields,
  LOCATION: 'Location',
} as const;

/** How the editor names each location a delay can wait for. */
export const LOCATION_TARGET_LABELS: Record<LocationTarget, string> = {
  [LocationTarget.LOCATION_TARGET_UNSPECIFIED]: 'a location',
  [LocationTarget.ORIGIN]: 'Origin',
  [LocationTarget.DESTINATION]: 'Destination',
  [LocationTarget.CONTROLLING_OFFICE]: 'Controlling office',
  [LocationTarget.PICKUP_OFFICE]: 'Pickup office (Pickup RTG)',
  [LocationTarget.DROP_OFFICE]: 'Drop office (Drop RTG)',
  [LocationTarget.UNRECOGNIZED]: 'a location',
};

const DAY_SHORT_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const WEEKDAYS = [
  DayOfWeek.MONDAY,
  DayOfWeek.TUESDAY,
  DayOfWeek.WEDNESDAY,
  DayOfWeek.THURSDAY,
  DayOfWeek.FRIDAY,
];

const WEEKENDS = [DayOfWeek.SUNDAY, DayOfWeek.SATURDAY];

function arraysEqual<T>(a: T[], b: T[]): boolean {
  return a.length === b.length && a.every((val, i) => val === b[i]);
}

/**
 * Format days of week for display (e.g., "Mon, Wed, Fri" or "Weekdays")
 */
function formatDaysOfWeek(days: DayOfWeek[]): string {
  if (days.length === 0) return '';
  if (days.length === 7) return 'Every day';

  const sortedDays = [...days].sort((a, b) => a - b);

  if (arraysEqual(sortedDays, WEEKDAYS)) {
    return 'Weekdays';
  }

  if (arraysEqual(sortedDays, WEEKENDS)) {
    return 'Weekends';
  }

  return sortedDays.map((d) => DAY_SHORT_NAMES[d]).join(', ');
}

/**
 * Format the delay configuration for display in the node subtitle.
 */
function formatDuration(data: LBNodeData): string {
  const delayData = data.delay_action;

  // Handle "until_event" case
  if (delayData?.until_event !== undefined) {
    return untilEventSubtitle(delayData.until_event);
  }

  if (delayData?.until_location_open !== undefined) {
    const target = delayData.until_location_open.target;
    if (target === LocationTarget.LOCATION_TARGET_UNSPECIFIED) {
      return '(unconfigured)';
    }
    return `Until ${LOCATION_TARGET_LABELS[target]} opens`;
  }

  // Handle "until_day" case
  if (delayData?.until_day !== undefined) {
    const { days_of_week, seconds_since_midnight } = delayData.until_day;
    if (seconds_since_midnight === undefined) {
      return '(unconfigured)';
    }
    const timeStr = formatUtcSecondsAsUserTime(seconds_since_midnight);
    const daysStr = formatDaysOfWeek(days_of_week);
    return `Until ${timeStr} on ${daysStr}`;
  }

  // Handle "until_time" case
  if (delayData?.until_time !== undefined) {
    if (delayData.until_time.seconds_since_midnight === undefined) {
      return '(unconfigured)';
    }
    const timeStr = formatUtcSecondsAsUserTime(
      delayData.until_time.seconds_since_midnight
    );
    return `Until ${timeStr}`;
  }

  // Handle "duration" case
  if (!delayData?.duration?.seconds) {
    return '(unconfigured)';
  }

  const unit = delayData.duration.display_unit ?? IntervalUnit.MINUTES;
  const amount = secondsToAmount(delayData.duration.seconds, unit);

  const unitLabel =
    unit === IntervalUnit.HOURS
      ? 'hour'
      : unit === IntervalUnit.SECONDS
        ? 'second'
        : 'minute';
  const pluralizedUnit = amount === 1 ? unitLabel : `${unitLabel}s`;

  return `For ${amount} ${pluralizedUnit}`;
}

export const DelayActionNodeInfo: LBNodeInfo = {
  protobufNodeDataKey: 'delay_action',
  category: NodeCategory.CONTROL_FLOW,
  label: 'Delay',
  hasInput: true,
  getIcon: (size: number) => (
    <Hourglass size={size} color={CONTROL_FLOW_ICON_COLOR} />
  ),
  outputType: OutputType.SINGLE,
  getSubtitle: (data: LBNodeData, _graph: GraphData) => {
    return formatDuration(data);
  },

  getValidationErrors: (
    _nodeId: string,
    nodeData: LBNodeData,
    graph: GraphData
  ) => {
    const delayData = nodeData.delay_action;

    // "until_event" mode - needs events, each with its required settings
    if (delayData?.until_event !== undefined) {
      return collectErrors(...validateUntilEvent(delayData.until_event, graph));
    }

    // "until_location_open" mode - requires a location
    if (delayData?.until_location_open !== undefined) {
      if (
        delayData.until_location_open.target ===
        LocationTarget.LOCATION_TARGET_UNSPECIFIED
      ) {
        return [
          {
            type: 'REQUIRED_FIELD',
            field: DelayActionFields.LOCATION,
            message: MSG_REQUIRED,
          },
        ];
      }
      return undefined;
    }

    // "until_day" mode - requires at least one day selected and a time picked
    if (delayData?.until_day !== undefined) {
      const errors = collectErrors(
        validateRequiredArray(
          delayData.until_day.days_of_week,
          DelayActionFields.DAYS_OF_WEEK
        )
      );
      if (delayData.until_day.seconds_since_midnight === undefined) {
        return [
          ...(errors ?? []),
          {
            type: 'REQUIRED_FIELD',
            field: DelayActionFields.TIME,
            message: MSG_REQUIRED,
          },
        ];
      }
      return errors;
    }

    // "until_time" mode - requires a time picked
    if (delayData?.until_time !== undefined) {
      if (delayData.until_time.seconds_since_midnight === undefined) {
        return [
          {
            type: 'REQUIRED_FIELD',
            field: DelayActionFields.TIME,
            message: MSG_REQUIRED,
          },
        ];
      }
      return undefined;
    }

    // "duration" mode - requires a positive duration
    if (!delayData?.duration?.seconds || delayData.duration.seconds <= 0) {
      return [
        {
          type: 'REQUIRED_FIELD',
          field: DelayActionFields.DELAY_DURATION,
          message: MSG_REQUIRED,
        },
      ];
    }

    return undefined;
  },

  getRunStepSummary: getDelayRunStepSummary,

  CustomizationComponent: DelayActionCustomization,
  createDefaultNodeData: () =>
    buildNodeData('delay_action', DEFAULT_DELAY_ACTION),
};
